/// <amd-module name="ServiceHooks/Web/Admin/Components/FormInput/Form" />
import { InputDescriptor, InputValues } from "ServiceHooks/Web/Admin/Contracts/FormInput";
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { sanitizeLinks } from "Utilities";
import { IObservableValue, ObservableValue, useObservable } from "VSS/Core/Observable";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import * as React from "react";
import { IFormGroup, IFormProps } from "./Form.Props";
import { FormInput, validateFormInput } from "./FormInput";
import { IValidationResult } from "./FormInput.Props";

export function validateForm(dataModel: { [key: string]: string }, inputDescriptors: InputDescriptor[]): boolean {
    return inputDescriptors.every(descriptor => validateFormInput(descriptor, dataModel[descriptor.id]).valid);
}

export function createValidityMap(dataModel: { [key: string]: string }, inputDescriptors: InputDescriptor[]) {
    const dictionary: { [key: string]: IValidationResult } = {};
    inputDescriptors.forEach(descriptor => (dictionary[descriptor.id] = validateFormInput(descriptor, dataModel[descriptor.id] || "")));
    return dictionary;
}

/**
 * Maps a given input descriptor to all descriptors that depend on it
 * @param inputDescriptors
 */
function createDependencyMap(inputDescriptors: InputDescriptor[]) {
    const map: { [key: string]: InputDescriptor[] } = {};

    // Add all descriptor IDs to the map
    inputDescriptors.forEach(descriptor => {
        map[descriptor.id] = [];
    });

    // Add all dependencies
    inputDescriptors.forEach(descriptor => {
        descriptor.dependencyInputIds &&
            descriptor.dependencyInputIds.forEach(dependencyId => {
                map[dependencyId].push(descriptor);
            });
    });

    return map;
}

function flattenDescriptorGraph(inputDescriptors: InputDescriptor[]) {
    const newOrder: InputDescriptor[] = [];

    const addToNewOrder = (inputDescriptor: InputDescriptor | undefined) => {
        if (!inputDescriptor || newOrder.indexOf(inputDescriptor) !== -1) {
            return;
        }

        if (inputDescriptor.dependencyInputIds) {
            inputDescriptor.dependencyInputIds.forEach(dependencyId =>
                addToNewOrder(inputDescriptors.find(testDescriptor => testDescriptor.id === dependencyId))
            );
        }

        newOrder.push(inputDescriptor);
    };

    inputDescriptors.forEach(descriptor => addToNewOrder(descriptor));
    return newOrder;
}

function groupDescriptors(inputDescriptors: InputDescriptor[]) {
    const dictionary: { [key: string]: IFormGroup } = {};
    const list: IFormGroup[] = [];

    inputDescriptors.forEach(descriptor => {
        const group = descriptor.groupName || "";

        if (!dictionary[group]) {
            dictionary[group] = { name: group, inputDescriptors: [] };
            list.push(dictionary[group]);
        }
        dictionary[group].inputDescriptors.push(descriptor);
    });

    return list;
}

function createLoadingMap(inputDescriptors: InputDescriptor[]) {
    const map: { [key: string]: IObservableValue<boolean> } = {};
    inputDescriptors.forEach(descriptor => (map[descriptor.id] = new ObservableValue(false)));
    return map;
}

const useForceUpdate = () => {
    const [, setIt] = React.useState(false);
    return () => setIt(it => !it);
};

export const Form: React.FunctionComponent<IFormProps> = props => {
    const { inputDescriptors, inputValues, onValidate, onItemValueChange, onFetchInputDetails, title } = props;

    const forceUpdate = useForceUpdate();
    const [validityMap, setValidityMap] = React.useState(() => createValidityMap(inputValues, inputDescriptors));
    const dependencyMap = createDependencyMap(inputDescriptors);

    const [inputDetailsLoaded, setInputDetailsLoaded] = useObservable(false);
    const [displaySpinner, setDisplaySpinner] = useObservable(false);
    const [errorMessage, setErrorMessage] = useObservable("");
    const sortedDescriptors = flattenDescriptorGraph(inputDescriptors);

    const [loadingMap, setLoadingMap] = React.useState<{ [key: string]: IObservableValue<boolean> }>(() => createLoadingMap(inputDescriptors));

    React.useEffect(() => {
        const dynamicDescriptors = inputDescriptors.filter(descriptor => descriptor.hasDynamicValueInformation);

        if (onFetchInputDetails && dynamicDescriptors.length > 0) {
            const spinnerTimeoutId = setTimeout(() => {
                setDisplaySpinner(true);
            }, 200);

            onFetchInputDetails(
                dynamicDescriptors.map(descriptor => {
                    return { inputId: descriptor.id } as InputValues;
                })
            )
                .then(result => {
                    result.forEach(item => {
                        const descriptor = dynamicDescriptors.find(x => x.id === item.inputId);
                        if (descriptor) {
                            descriptor.values = item;
                        }
                    });

                    clearTimeout(spinnerTimeoutId);
                    setDisplaySpinner(false);

                    setValidityMap(createValidityMap(inputValues, inputDescriptors));
                    setInputDetailsLoaded(true);
                    onValidate(validateForm(inputValues, inputDescriptors));
                })
                .catch(reason => {
                    setErrorMessage(reason.message as string);
                });
        } else {
            setInputDetailsLoaded(true);
            onValidate(validateForm(inputValues, inputDescriptors));
            $DEBUG &&
                dynamicDescriptors.length > 0 &&
                console.error("Form needed to load dynamic descriptors but didn't have a method to fetch details");
        }
    }, []);

    const requestValues = (descriptors: InputDescriptor[]) => {
        // Done this way for easy recursion
        const updateLoadingValue = (descriptor: InputDescriptor) => {
            if (loadingMap[descriptor.id]) {
                loadingMap[descriptor.id].value = true;
            } else {
                loadingMap[descriptor.id] = new ObservableValue(true);
            }
            dependencyMap[descriptor.id].forEach(transitiveDependency => {
                updateLoadingValue(transitiveDependency);
            });
        };
        descriptors.forEach(updateLoadingValue);
        setLoadingMap(loadingMap); // Force an update to propagate the new observable down in the event form items change

        return onFetchInputDetails(descriptors.map(descriptor => ({ inputId: descriptor.id } as InputValues))).then(result => {
            descriptors.forEach(descriptor => {
                loadingMap[descriptor.id].value = false;
            });
            return result;
        });
    };

    const handleValueChange = (descriptor: InputDescriptor, newValue: string | undefined) => {
        onItemValueChange(descriptor, newValue);
        onValidate(validateForm(inputValues, inputDescriptors));
        const newValidityMap = createValidityMap(inputValues, inputDescriptors);

        const fieldsToRefresh = dependencyMap[descriptor.id];
        if (fieldsToRefresh.length > 0) {
            if (newValidityMap[descriptor.id].valid) {
                requestValues(fieldsToRefresh)
                    .then(returnedValues => {
                        returnedValues.forEach(returnedValue => (fieldsToRefresh.find(x => x.id === returnedValue.inputId)!.values = returnedValue));
                        forceUpdate();
                    })
                    .catch(reason => {
                        setErrorMessage(reason.message as string);
                    });
            } else {
                fieldsToRefresh.forEach(x => {
                    x.values = { ...x.values, possibleValues: [] };
                    dependencyMap[x.id].forEach(y => (y.values = { ...y.values, possibleValues: [] }));
                });
            }
        }

        setValidityMap(newValidityMap);
    };

    return (
        <div className="hooks-form-input-group flex-column flex-self-stretch rhythm-vertical-8 padding-vertical-16">
            <Observer errorMessage={errorMessage}>
                {(observerProps: { errorMessage: string }) => {
                    return observerProps.errorMessage.length > 0 ? (
                        <MessageCard severity={MessageCardSeverity.Error}>{sanitizeLinks(observerProps.errorMessage)}</MessageCard>
                    ) : null;
                }}
            </Observer>

            <ConditionalChildren renderChildren={inputDetailsLoaded}>
                {...groupDescriptors(sortedDescriptors).map(group => {
                    return (
                        <>
                            {title && (<div className="title-s margin-top-16">{title}</div>)}
                            <div key={group.name} className="hooks-form-input-group rhythm-vertical-8">
                                {group.name !== "" && <div className="hooks-form-input-group-title title-s">{group.name}</div>}
                                {group.inputDescriptors.map(descriptor => (
                                    <FormInput
                                        key={descriptor.id}
                                        inputDescriptor={descriptor}
                                        loading={loadingMap[descriptor.id]}
                                        onValueChange={handleValueChange.bind(null, descriptor)}
                                        validity={validityMap[descriptor.id] || false}
                                        value={inputValues[descriptor.id] || ""}
                                    />
                                ))}
                            </div>
                        </>
                    );
                })}
                {props.children}
            </ConditionalChildren>

            <ConditionalChildren renderChildren={displaySpinner}>
                <Spinner className="hooks-form-input-loading-spinner flex-self-center" label={Resources.Loading} size={SpinnerSize.large} />
            </ConditionalChildren>
        </div>
    );
};
